/**
 * View-pose computation for the Verification workspace.
 *
 * Two consumers — `ViewerStatusBar` (user clicks a view) and
 * `VerificationViewer` (preflight applies the default Iso view). Both call
 * through the same pure functions here, so there's one canonical place the
 * local-frame math lives.
 *
 * ### The six ortho views are WORLD-FIXED. Only Isometric uses the local frame.
 *
 * The named views are labelled with the app's axis vocabulary — Eastings (X),
 * Northings (Y), Elevation (Z) — and each one declares the slice plane it
 * captures (`verificationContext.VERIFICATION_WORKSPACE_VIEWS[].sub`). That
 * declaration is consumed as fact: `PLANE_AXES` (asBuiltDraft) decides which
 * two axes a pick is allowed to write, and `PLANE_DEPTH_AXIS` (axisLock)
 * decides which axis RULE 3 refuses to lock.
 *
 * So the declared plane MUST equal the plane actually on screen. Deriving the
 * ortho directions from `computeLocalFrame` broke that: the frame is picked
 * from the crop box's LONGEST dimension, so on a Y-dominant tag the old
 * `elevation` (declared XZ) actually looked along −X and the old `section`
 * (declared YZ) actually looked along −Y — the two views were swapped against
 * the planes the app claimed. The consequences were not cosmetic: axisLock
 * RULE 3 fires on the wrong axis, and a depth-derived coordinate (up to 656 mm
 * out on a silhouette edge) gets recorded as a measured in-plane axis. The
 * demo support is X-dominant, which is why nobody hit it.
 *
 * A world axis cannot flip with the bounding box, so the six ortho poses below
 * are hard-wired to world axes and the declaration is true by construction.
 *
 * Isometric keeps `computeLocalFrame` — it is navigation only, can never
 * resolve an axis (`asBuiltDraft.PLANE_3D`), and its local framing is nicer.
 *
 * Naming inside `computeLocalFrame` is intentionally **domain-generic**:
 * `primaryAxis` + `secondaryAxis` rather than `pipeAxis` / `across`. The
 * algorithm works for any tag whose long dimension is horizontal (pipes today,
 * beams / cable trays / conduits tomorrow) — nothing here is pipe-specific.
 * Vertical-dominant tags fall back to world X as the primary axis.
 *
 * ### Forge / THREE.js compatibility
 *
 * Forge Viewer installs an LMV-bundled THREE.js r71 to `window.THREE`. That
 * build's `Vector3` lacks newer methods (`addScaledVector`, `normalize`,
 * `clone` chaining). All math below uses plain `{x, y, z}` objects and
 * primitive arithmetic — `window.THREE.Vector3` is instantiated only via
 * its constructor at the return boundary, where `viewer.navigation.setView`
 * needs a real Vector3 instance.
 */

/**
 * Build the verified tag's **local orthonormal frame** from its crop box.
 *
 * Returns plain `{x, y, z}` direction vectors (not Vector3 instances) so
 * downstream callers can do arithmetic without relying on Vector3 methods.
 *
 * @param {{ getCenter: Function, getSize: Function }} cropBox  — THREE.Box3
 *   or a Forge LMV Box3 (both expose `.getCenter(target)` and `.getSize(target)`).
 * @returns {{
 *   tagCenter: { x: number, y: number, z: number },
 *   primaryAxis: { x: number, y: number, z: number },
 *   secondaryAxis: { x: number, y: number, z: number },
 *   up: { x: number, y: number, z: number },
 *   distance: number,
 * } | null}
 */
export function computeLocalFrame(cropBox) {
  const THREE = window.THREE;
  if (!THREE || !cropBox) return null;

  const rawCenter = cropBox.getCenter(new THREE.Vector3());
  const rawSize = cropBox.getSize(new THREE.Vector3());
  const tagCenter = { x: rawCenter.x, y: rawCenter.y, z: rawCenter.z };
  const size = { x: rawSize.x, y: rawSize.y, z: rawSize.z };
  const diagonal = Math.sqrt(size.x * size.x + size.y * size.y + size.z * size.z);
  // The floor is ONE VIEWER UNIT, not one metre — a millimetre model makes it a
  // millimetre. That is fine here and deliberately left alone: it exists only so
  // a degenerate (zero-size) box cannot put the camera exactly on its target,
  // and every real box's diagonal dwarfs it on either kind of model. It is a
  // divide-by-zero guard, not a distance anyone should read as a length.
  const distance = Math.max(diagonal * 2, 1);
  const up = { x: 0, y: 0, z: 1 };

  // Identify the longest dimension. If it's vertical (Z), the "primary is
  // the longest horizontal" premise breaks — fall back to world X as proxy.
  // Views still render as valid orthographic projections; they just lose
  // their "pipe-aligned" flavor for vertical-dominant tags.
  const isVerticalDominant = size.z >= size.x && size.z >= size.y;
  let primaryAxis;
  let secondaryAxis;
  if (isVerticalDominant) {
    primaryAxis = { x: 1, y: 0, z: 0 };
    secondaryAxis = { x: 0, y: 1, z: 0 };
  } else if (size.x >= size.y) {
    // Primary axis along world X → secondary = primary × up = (0, -1, 0)
    primaryAxis = { x: 1, y: 0, z: 0 };
    secondaryAxis = { x: 0, y: -1, z: 0 };
  } else {
    // Primary axis along world Y → secondary = primary × up = (1, 0, 0)
    primaryAxis = { x: 0, y: 1, z: 0 };
    secondaryAxis = { x: 1, y: 0, z: 0 };
  }

  return { tagCenter, primaryAxis, secondaryAxis, up, distance };
}

/**
 * The six world-fixed ortho views.
 *
 * `dir` points from the tag centre TOWARDS the camera, so the camera looks
 * along `-dir`. The view id names THE FACE YOU SEE, which is the same thing as
 * where the camera stands: `north` puts the camera north of the tag looking
 * south, showing its north face.
 *
 * `-dir` is therefore the view's DEPTH axis — the one axis it cannot measure —
 * and it must agree with `axisLock.PLANE_DEPTH_AXIS[capturePlaneForView(id)]`
 * for every entry. That equivalence is the whole point of this table; if you
 * edit a direction here, re-check `asBuiltDraft.CAPTURE_PLANE_BY_VIEW`.
 *
 * WHY BOTH PLAN VIEWS USE up = +Y. North reads UP the screen on `top`, which is
 * the drawing convention every surveyor already has, and it matches the north
 * arrow the axis triad draws at +Y (`axisTriad.js`). Carrying the same up onto
 * `bottom` mirrors it left-to-right — that is inherent to looking at an
 * underside, not a bug. Flipping `bottom` to -Y would keep east on the right
 * but put north DOWN, which is worse: it silently contradicts the north arrow
 * in the same viewport. Do not "fix" it.
 */
const WORLD_ORTHO_VIEWS = Object.freeze({
  top: Object.freeze({ dir: { x: 0, y: 0, z: 1 }, up: { x: 0, y: 1, z: 0 } }),
  bottom: Object.freeze({ dir: { x: 0, y: 0, z: -1 }, up: { x: 0, y: 1, z: 0 } }),
  north: Object.freeze({ dir: { x: 0, y: 1, z: 0 }, up: { x: 0, y: 0, z: 1 } }),
  south: Object.freeze({ dir: { x: 0, y: -1, z: 0 }, up: { x: 0, y: 0, z: 1 } }),
  east: Object.freeze({ dir: { x: 1, y: 0, z: 0 }, up: { x: 0, y: 0, z: 1 } }),
  west: Object.freeze({ dir: { x: -1, y: 0, z: 0 }, up: { x: 0, y: 0, z: 1 } }),
});

/** The isometric view id — the only one still posed from the local frame. */
const ISO_VIEW_ID = 'iso';

/**
 * Per-view camera pose. Result is ready to pass straight to
 * `viewer.navigation.setView(position, target, upVector)`.
 *
 * Known view ids: the six keys of `WORLD_ORTHO_VIEWS` plus `'iso'`. These MUST
 * stay in step with `verificationContext.VERIFICATION_WORKSPACE_VIEWS` — labels
 * live in that data array and poses live here, and an id present in one but not
 * the other fails SILENTLY: `ViewerStatusBar.applyView` just logs
 * "unknown viewId" and the camera does not move.
 *
 * The crop box no longer sets ortho ORIENTATION, but it is still required: it
 * supplies `distance` (from the box diagonal) via `computeLocalFrame`, and Iso
 * still uses its full frame.
 *
 * @param {string} viewId
 * @param {object} cropBox  — stable reference box for distance + the Iso frame
 * @param {object} [targetBox=cropBox] — optional mutable box whose centre the
 *   camera should target; its dimensions never influence orientation
 * @returns {{ position: object, target: object, upVector: object } | null}
 */
export function computeViewPose(viewId, cropBox, targetBox = cropBox) {
  const THREE = window.THREE;
  const frame = computeLocalFrame(cropBox);
  if (!THREE || !frame) return null;

  const { primaryAxis, secondaryAxis, up, distance } = frame;
  const rawTarget = targetBox?.getCenter?.(new THREE.Vector3());
  const tagCenter = rawTarget
    ? { x: rawTarget.x, y: rawTarget.y, z: rawTarget.z }
    : frame.tagCenter;

  // Constructor-only Vector3 creation + pure-arithmetic offsets. Never call
  // methods on THREE.Vector3 — the LMV-bundled build (r71) strips several.
  const vec = (x, y, z) => new THREE.Vector3(x, y, z);
  const offsetFrom = (dir, scale) => vec(
    tagCenter.x + dir.x * scale,
    tagCenter.y + dir.y * scale,
    tagCenter.z + dir.z * scale,
  );
  const target = vec(tagCenter.x, tagCenter.y, tagCenter.z);

  // The six ortho views: world axes, never the local frame. See the table's
  // docblock for why the direction cannot be derived from the crop box.
  const ortho = WORLD_ORTHO_VIEWS[viewId];
  if (ortho) {
    return {
      position: offsetFrom(ortho.dir, distance),
      target,
      upVector: vec(ortho.up.x, ortho.up.y, ortho.up.z),
    };
  }

  if (viewId === ISO_VIEW_ID) {
    // 45° NE-above in LOCAL frame — sum the three basis unit vectors, normalize
    // by magnitude. Hand-rolled to avoid Vector3.normalize().
    //
    // Iso keeps the local frame deliberately. Its up-vector is `up` and the
    // frame's `secondaryAxis` is horizontal-perpendicular to `primaryAxis`, so
    // the tag's long direction reads left-to-right across a landscape viewport
    // instead of top-to-bottom. That framing is worth keeping precisely because
    // Iso is navigation only — it never resolves an axis, so its orientation
    // carries no measurement claim that a bounding-box-derived frame could
    // falsify. The ortho views cannot afford the same freedom.
    const sx = up.x + secondaryAxis.x + primaryAxis.x;
    const sy = up.y + secondaryAxis.y + primaryAxis.y;
    const sz = up.z + secondaryAxis.z + primaryAxis.z;
    const len = Math.sqrt(sx * sx + sy * sy + sz * sz) || 1;
    const isoDir = { x: sx / len, y: sy / len, z: sz / len };
    return {
      position: offsetFrom(isoDir, distance),
      target,
      upVector: vec(up.x, up.y, up.z),
    };
  }

  return null;
}

export default computeViewPose;
